import {Group, Title} from '@mantine/core';
import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {getArchivedTooltipMessage} from 'analysis-tool/AnalysisToolMessages';
import {AnalysisRightSidebarItem} from 'analysis-tool/right-sidebar/RightSidebarTypes';
import AddDataButton from 'analysis-tool/ui/header/AddDataButton';
import RefreshDataButton from 'analysis-tool/ui/header/RefreshDataButton';
import {AnalysisMainViewItem, AnalysisMainViewItemType} from 'analysis-tool/ui/main-view/MainViewTypes';
import ToggleAnalysisSidebarButton from 'analysis-tool/ui/sidebar/ToggleAnalysisSidebarButton';
import isArchived from 'archive/isArchived';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import getTestProps from 'test/lib/cypress/getTestProps';
import {InlineBannerProps} from 'ui-library/banners/InlineBanner';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';
import Breadcrumb, {BreadcrumbItem} from 'ui-library/Breadcrumb';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT} from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import OverflowMenu, {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';

/** @package */
export const READ_ONLY_TOOLTIP_TEXT = 'For edit access please contact the project owner.';

interface BaseAnalysisUIHeaderProps {
  pipeline: PipelineGraphQL;
  showOpenSidebarButton: boolean;
  onChangeSelectedItem: (newSelectedItem: AnalysisMainViewItem) => void;
  onOpenRightSidebarItem?: (newSelectedRightSidebarItem: AnalysisRightSidebarItem | null) => void;
  onOpenSidebar: () => void;
  onOpenStepInitializationView: (newSelectedItem: AnalysisMainViewItem) => void;
  /** Message to show as top-level banners. */
  bannerStackMessages: Array<InlineBannerProps>;
  /** Overflow menu options */
  overflowMenuOptions: Array<OverflowMenuOption>;
  /** Whether outputs can be sent from the pipeline. */
  canCreateOutputs?: boolean;
  /** Items to go in the header breadcrumbs. */
  breadcrumbInfo: {items: Array<BreadcrumbItem>; icon: BenchlingIconName};
  /** Content shown in the center of the header, between the title on the left and menu on the right. */
  children?: React.ReactNode;
  /** Determines whether to show the refresh button (for example allows analyses to show refresh, but not templates). */
  showRefreshDataButton: boolean;
}

/** @package */
export default function BaseAnalysisUIHeader({
  pipeline,
  showOpenSidebarButton,
  onChangeSelectedItem,
  onOpenRightSidebarItem,
  onOpenSidebar,
  onOpenStepInitializationView,
  overflowMenuOptions,
  canCreateOutputs,
  breadcrumbInfo,
  bannerStackMessages,
  children,
  showRefreshDataButton,
}: BaseAnalysisUIHeaderProps): JSX.Element {
  return (
    <div {...getTestProps({component: 'AnalysisUIHeader', element: 'root'})}>
      {bannerStackMessages.length > 0 && (
        <div className={css(styles.bannerStack)}>
          <InlineBannerStack messages={bannerStackMessages} />
        </div>
      )}
      <div className={css(styles.contentWrapper)}>
        <div className={css(styles.leftContentWrapper)}>
          {showOpenSidebarButton && (
            <div className={css(styles.expandSidebarWrapper)}>
              <ToggleAnalysisSidebarButton sidebarIsOpen={false} toggleSidebar={onOpenSidebar} />
            </div>
          )}
          <div>
            <div
              className={css(styles.breadcrumbWrapper)}
              {...getTestProps({component: 'AnalysisHeaderBreadcrumb', element: 'root'})}
            >
              <BenchlingIcon icon={breadcrumbInfo.icon} className={css(styles.breadcrumbIcon)} />
              <Breadcrumb items={breadcrumbInfo.items} isLastItemInteractable={true} />
            </div>
            <Group>
              <Title order={4}>{pipeline.name}</Title>
            </Group>
          </div>
        </div>
        {children && <div className={css(styles.centerContentWrapper)}>{children}</div>}
        <div className={css(styles.rightContentWrapper)}>
          <OverflowMenu
            buttonProps={{
              size: ButtonSize.SMALL,
              buttonStyle: ButtonStyle.TERTIARY_GRAY,
              dataTestAttrs: {component: 'AnalysisUIHeader', element: 'overflowMenu'},
            }}
            enableTooltip={true}
            menuProps={{dropLeft: true}}
            options={overflowMenuOptions}
            tooltipPlacement={FloatingPlacement.LEFT}
          />
          <div className={css(styles.headerSeparator)} />
          {showRefreshDataButton && (
            <div className={css(styles.headerButton)}>
              <RefreshDataButton analysis={pipeline} />
            </div>
          )}
          <FlowchartButton onChangeSelectedItem={onChangeSelectedItem} />
          {canCreateOutputs && (
            <CreateOutputsButton pipeline={pipeline} onChangeSelectedItem={onChangeSelectedItem} />
          )}
          {(pipeline.permissions.writable || pipeline.permissions.unarchivable) && (
            <div className={css(styles.headerButton)}>
              <AddDataButton
                actionOrigin={ActionOrigin.ANALYSIS_TOOL_HEADER}
                analysis={pipeline}
                buttonSize={ButtonSize.SMALL}
                onOpenStepInitializationView={onOpenStepInitializationView}
                onOpenRightSidebarItem={onOpenRightSidebarItem}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CreateOutputsButton({
  pipeline,
  onChangeSelectedItem,
}: Pick<BaseAnalysisUIHeaderProps, 'pipeline' | 'onChangeSelectedItem'>): JSX.Element {
  return (
    <Tooltip text={isArchived(pipeline) ? getArchivedTooltipMessage(pipeline) : null}>
      <Button
        buttonStyle={ButtonStyle.TERTIARY_BLUE}
        icon="send"
        size={ButtonSize.SMALL}
        text="Send output"
        disabled={isArchived(pipeline)}
        onClick={() => {
          onChangeSelectedItem({type: AnalysisMainViewItemType.CREATE_OUTPUTS});
        }}
        className={css(styles.headerButton)}
        dataTestAttrs={{component: 'AnalysisCreateOutputsButton', element: 'root'}}
      />
    </Tooltip>
  );
}

function FlowchartButton({
  onChangeSelectedItem,
}: Pick<BaseAnalysisUIHeaderProps, 'onChangeSelectedItem'>): JSX.Element {
  return (
    <Button
      buttonStyle={ButtonStyle.TERTIARY_GRAY}
      icon="workflow"
      size={ButtonSize.SMALL}
      text="Flowchart"
      onClick={() => {
        onChangeSelectedItem({type: AnalysisMainViewItemType.FLOWCHART});
      }}
      className={css(styles.headerButton)}
    />
  );
}

const styles = StyleSheet.create({
  contentWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  leftContentWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  centerContentWrapper: {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    paddingLeft: 12,
    paddingRight: 12,
  },
  rightContentWrapper: {
    display: 'flex',
    flexShrink: 0,
    alignItems: 'center',
    paddingLeft: 6,
  },
  expandSidebarWrapper: {
    marginRight: 12,
  },
  bannerStack: {
    marginBottom: 15,
  },
  headerButton: {
    marginLeft: 6,
    flexShrink: 0,
  },
  headerSeparator: {
    width: 1,
    height: 27,
    backgroundColor: GRAY_LIGHT,
    marginLeft: 6,
    flexShrink: 0,
  },
  breadcrumbWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  breadcrumbIcon: {
    marginRight: 6,
  },
});
